import { useAuth0 } from "@auth0/auth0-react";
import { ArrowRight, CaretDown, CaretUp, Fog } from "@carbon/icons-react";
import { Electric, Faucet, Folder, Humid } from "@carbon/pictograms-react";
import {
  Button, InlineNotification, SkeletonPlaceholder, SkeletonText, StructuredListBody,
  StructuredListCell, StructuredListHead, StructuredListRow, StructuredListSkeleton,
  StructuredListWrapper, Tab, TabList, TabPanel, TabPanels, Tabs
} from "@carbon/react";
import axios from "axios";
import Color from "color";
import { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import BarChart from "../../components/BarChart";
import PageTitleBlock from "../../components/PageTitleBlock/PageTitleBlock";
import SelectReportDate from "../../components/SelectReportDate/SelectReportDate";
import ReportDateContext from "../../ReportDateContext";

import { ReactComponent as Flame } from "../../static/gas.svg";
import wspColor from "../../wspColours.scss";

const BuildingPortfolio = () => {
  const { getAccessTokenSilently } = useAuth0();

  const location = useLocation();
  const navigate = useNavigate();
  const { year, month, useActual, selectedReport, availableReports, setReportDate, setUseActual } = useContext(ReportDateContext);
  const [loading, setLoading] = useState(true);
  const [buildingPortfolioName, setBuildingPortfolioName] = useState("");
  const [portfolioEUI, setPortfolioEUI] = useState(0);
  const [portfolioEUIChange, setPortfolioEUIChange] = useState(0);
  const [portfolioWUI, setPortfolioWUI] = useState(0);
  const [portfolioWUIChange, setPortfolioWUIChange] = useState(0);
  const [buildingUIList, setBuildingUIList] = useState([]);
  const [portfolioEuiData, setPortfolioEuiData] = useState([]);
  const [portfolioWuiData, setPortfolioWuiData] = useState([]);

  if (year === null && month === null) {
    navigate("/"); // redirect to main page
  }

  useEffect(() => {
    const chartColors = [Color(wspColor.red01), Color(wspColor.charcoaldark)];
    const formatChartEntries = (chartEntries, chartLabels) =>
      chartEntries.map(d => ({
        utility: d.utility,
        chartData: {
          labels: chartLabels,
          datasets: Object.entries(d.data).map(([key, value], index) => ({
            label: key.replace("_", " "),
            data: value,
            backgroundColor: chartColors[index].alpha(0.5).string(),
            borderColor: chartColors[index].string(),
            borderWidth: 1
          }))
        }
      }));

    const getData = async () => {
      try {
        setLoading(true);
        const accessToken = await getAccessTokenSilently();
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_SERVER_URL}/data/building_portfolio/${location.state.group}/${year}/${month}${useActual ? "?actual" : ""}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`
            }
          }
        );

        // Set page data
        setBuildingPortfolioName(data.group_name);
        setPortfolioEUI(data.portfolio_eui_current);
        setPortfolioEUIChange(data.portfolio_eui_change);
        setPortfolioWUI(data.portfolio_wui_current);
        setPortfolioWUIChange(data.portfolio_wui_change);
        setBuildingUIList(data.building_ui_list);
        setPortfolioEuiData(formatChartEntries(data.eui_chart_data, data.eui_chart_labels));
        setPortfolioWuiData(formatChartEntries(data.wui_chart_data, data.wui_chart_labels));
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    getData();
  }, [location, month, year, useActual, getAccessTokenSilently]);

  /*
   * Page rendering functions
   */
  const pctChangeLabel = pctChange => (
    <p className="building-portfolio__pctChange">
      {pctChange > 0 ? <CaretUp size={32} title="up" /> : <CaretDown size={32} title="down" />}
      {`${Math.abs(Math.round(pctChange * 1000) / 10)}%`}
    </p>
  );

  const createUseIntensityVal = (label, val, change) => (
    <div>
      {loading ? (
        <SkeletonText paragraph lineCount={3} />
      ) : (
        <>
          <h4>{label}</h4>
          <p className="building-portfolio__portfolio_ui_value">{val}</p>
          <p className="building-portfolio__pctChange">
            {change > 0 ? (
              <CaretUp size={32} title="up" />
            ) : (
              <CaretDown size={32} title="down" />
            )}
            {`${Math.abs(Math.round(change * 1000) / 10)}% from previous year`}
          </p>
        </>
      )}
    </div>
  );

  const createChartTabs = (title, tabChartData, unitLabel) => (
    <div>
      <h3>{title}</h3>
      {loading ? (
        <SkeletonPlaceholder className="building-portfolio__skeleton_placeholder" />
      ) : (
        <Tabs>
          <TabList aria-label="List of tabs">
            {tabChartData.map(d => <Tab key={`tab-${d.utility}`}>{d.utility}</Tab>)}
          </TabList>
          <TabPanels>
            {tabChartData.map(d => (
              <TabPanel
                tabIndex={0}
                label={d.utility}
                key={`tabbed-chart-${d.utility}`}
              >
                <div>
                  <BarChart
                    chartId={`building-portfolio-barchart-ui-${d.utility}`}
                    chartData={d.chartData}
                    aspectRatio={1.5}
                    labelYAxis="Use Intensity"
                    unitYAxis={unitLabel}
                    legend
                  />
                </div>
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </div>
  );

  return (
    <>
      <SelectReportDate
        id="selectedReportDate"
        availableReports={availableReports}
        selectedReport={selectedReport}
        label="Available Reports"
        onChange={setReportDate}
        useActual={useActual}
        onChangeuseActual={setUseActual}
        light
      />

      <PageTitleBlock
        title={buildingPortfolioName}
        icon={<Folder />}
        breadcrumbs={[
          {
            label: "UBI Dashboard",
            url: "/"
          }
        ]}
        loading={loading}
      />

      <section className="cds--grid">
        <div className="cds--row">
          <div className="cds--col-lg-10 cds--col-md-8 building-portfolio__portfolio_main">
            <div>
              <h3>All Portfolio Buildings</h3>
              <div className="building-portfolio__overall_portfolio">
                {createUseIntensityVal("EUI", portfolioEUI, portfolioEUIChange)}
                {createUseIntensityVal("WUI", portfolioWUI, portfolioWUIChange)}
              </div>
              <h3>Buildings List</h3>
              {loading ? (
                <StructuredListSkeleton />
              ) : (
                <StructuredListWrapper>
                  <StructuredListHead>
                    <StructuredListRow head>
                      <StructuredListCell head>Building</StructuredListCell>
                      <StructuredListCell head>Utilities Used</StructuredListCell>
                      <StructuredListCell head>EUI</StructuredListCell>
                      <StructuredListCell head>WUI</StructuredListCell>
                      <StructuredListCell head> </StructuredListCell>
                    </StructuredListRow>
                  </StructuredListHead>
                  <StructuredListBody>
                    {buildingUIList.map(d => (
                      <StructuredListRow key={`building_entry-${d.bid}`}>
                        <StructuredListCell>
                          <h5>
                            <Link to="/building_report" state={{ building: d.bid }}>
                              {d.bname}
                              <sup>
                                {!(useActual || d.normalized) && "†"}
                              </sup>
                            </Link>
                          </h5>
                          <p>{`GFA: ${d.gfa.toLocaleString()} sq. ft`}</p>
                        </StructuredListCell>
                        <StructuredListCell>
                          <div className="building-portfolio__utility_icons">
                            {(d.has_electricity === 1) && (
                              <div
                                className="electricity"
                                title="Electricity"
                              >
                                <Electric />
                              </div>
                            )}
                            {(d.has_natural_gas === 1) && (
                              <div
                                className="natural_gas"
                                title="Natural Gas"
                              >
                                <Flame />
                              </div>
                            )}
                            {(d.has_steam === 1) && (
                              <div className="steam" title="Steam">
                                <Fog />
                              </div>
                            )}
                            {(d.has_chilled_water === 1) && (
                              <div
                                className="chilled_water"
                                title="Chilled Water"
                              >
                                <Humid />
                              </div>
                            )}
                            {(d.has_water === 1) && (
                              <div className="water" title="Water">
                                <Faucet />
                              </div>
                            )}
                          </div>
                        </StructuredListCell>
                        <StructuredListCell>
                          <p>{`${d.eui}${d.partial_eui ? "*" : ""}`}</p>
                          {pctChangeLabel(d.eui_change)}
                        </StructuredListCell>
                        <StructuredListCell>
                          <p>{d.wui}</p>
                          {pctChangeLabel(d.wui_change)}
                        </StructuredListCell>
                        <StructuredListCell>
                          <Link to="/building_report" state={{ building: d.bid }}>
                            <Button renderIcon={ArrowRight} size="sm">
                              Report
                            </Button>
                          </Link>
                        </StructuredListCell>
                      </StructuredListRow>
                    ))}
                  </StructuredListBody>
                </StructuredListWrapper>
              )}
            </div>

            <div className="building-portfolio__ui_notes">
              <InlineNotification
                title="Note:"
                kind="info"
                hideCloseButton
                lowContrast
              >
                * - denotes building usage intensity values calculated with a
                partial utility set (i.e. usage data for one or more utilities
                unavailable)
                {!useActual && (
                  <>
                    <br />
                    <>
                      † - denotes buildings where normalized usage data is
                      fully or partially unavailable; reported data contains
                      actual usage
                    </>
                  </>
                )}
              </InlineNotification>
            </div>
          </div>

          <div className="cds--col-lg-6 cds--col-md-8 building-portfolio__portfolio_secondary">
            <div>
              {createChartTabs("Portfolio EUI", portfolioEuiData, "ekWh/sq. ft")}
              {createChartTabs("Portfolio WUI", portfolioWuiData, "L/sq. ft")}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BuildingPortfolio;
